import { atom, useAtomValue, useSetAtom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery, queryClientAtom } from 'jotai-tanstack-query';
import keyBy from 'lodash/keyBy';

import { organizationHeader, organizationQueryKey } from '@cast/constants';
import { client } from '@cast/core';
import { Organization } from '@cast/types';

import { searchParamsAtom } from '@cast/infrastructure-navigation';

import { useOrganizationAccessCheck } from './useOrganizationAccessCheck';
import { useOrganizationRouteValidation } from './useOrganizationRouteValidation';
import { getOrganizationsQueryOptions } from './queries/useOrganizationsQuery';

const organizationSearchParamAtom = atom(
  (get) => {
    const searchParams = get(searchParamsAtom);
    return searchParams.get(organizationQueryKey);
  },
  (_, set, newOrgId: string) => {
    set(searchParamsAtom, (current) => {
      current.set(organizationQueryKey, newOrgId);
      return current;
    });
  }
);

const organizationsQueryAtom = atomWithSuspenseQuery((get) => {
  const initialOrganizationId = get(organizationSearchParamAtom);
  return getOrganizationsQueryOptions({ initialOrganizationId });
});

/**
 * Async atom that fetches organizations data.
 *
 * This atom subscribes to organizationsQueryAtom for cache synchronization
 * while using queryClient.fetchQuery() to properly propagate Suspense.
 *
 * Why both?
 * - get(organizationsQueryAtom) - subscribes to TanStack Query cache updates
 * - queryClient.fetchQuery() - returns a Promise for Suspense propagation in async atoms
 *
 * Note: This is a workaround for a known Jotai limitation where atomWithSuspenseQuery
 * doesn't properly propagate Suspense to dependent async atoms.
 * See: https://github.com/pmndrs/jotai/discussions/2557
 */
const organizationsAtom = atom<Promise<Organization[]>>(async (get) => {
  const queryClient = get(queryClientAtom);
  // Subscribe to cache updates
  get(organizationsQueryAtom);
  const initialOrganizationId = get(organizationSearchParamAtom);
  // Fetch data for Suspense
  return await queryClient.fetchQuery(
    getOrganizationsQueryOptions({ initialOrganizationId })
  );
});

/**
 * Unwrapped organizations atom that returns previous value during refetch.
 *
 * CRITICAL: Uses (prev) => prev ?? [] fallback strategy:
 * - During initial load: prev is undefined, returns []
 * - During refetch: prev contains previous data, returns it (avoids flicker)
 * - After fetch completes: returns fresh data
 *
 * This prevents empty array returns during organization switching when cache is cleared.
 */
const organizationsAtomUnwrapped = unwrap(
  organizationsAtom,
  (prev) => prev ?? []
);

export const useOrganizations = () => useAtomValue(organizationsAtomUnwrapped);

const organizationKeyByIdAtom = atom((get) => {
  const organizations = get(organizationsAtomUnwrapped);
  return keyBy(organizations, 'id');
});

const useOrganizationKeyById = () => useAtomValue(organizationKeyByIdAtom);

/**
 * Mutual exclusion flag to prevent concurrent storage writes.
 * In-memory only - doesn't need to be an atom.
 */
let isUpdatingStorage = false;

/**
 * Helper to update all storage locations and axios headers with mutual exclusion.
 * Prevents race condition where concurrent updates could cause stale data overwrites.
 */
const updateStorageAndHeaders = (orgId: string): void => {
  // Guard: prevent concurrent writes
  if (isUpdatingStorage) {
    console.error(
      '[Storage] Blocked concurrent write - this may indicate a race condition. Consider investigating call stack to prevent duplicate updates.'
    );
    return;
  }

  try {
    isUpdatingStorage = true;

    // Update axios header
    client.defaults.headers.common[organizationHeader] = orgId;

    // Update browser storage
    sessionStorage.setItem(organizationQueryKey, orgId);
    localStorage.setItem(organizationQueryKey, orgId);
  } finally {
    isUpdatingStorage = false;
  }
};

/**
 * Internal primitive atom - stores the current organization ID override.
 * When undefined, the atom will resolve from storage/URL.
 * Not exported - use currentOrganizationIdAtom instead.
 */
const _currentOrganizationIdAtom = atom<Organization['id'] | undefined>(
  undefined
);

/**
 * Internal primitive atom - stores the viewed organization ID override.
 * When undefined, the atom will resolve to the default (parentId || currentOrganizationId).
 * Not exported - use viewedOrganizationIdAtom instead.
 */
const _viewedOrganizationIdAtom = atom<Organization['id'] | undefined>(
  undefined
);

/**
 * Main async atom for current organization ID with side effects.
 *
 * Read behavior (async):
 * - If _currentOrganizationIdAtom has a value, returns it immediately
 * - Otherwise, resolves organization ID based on priority hierarchy:
 *   1. Organization ID from location search params
 *   2. Organization ID from localStorage
 *   3. Organization ID from sessionStorage
 *   4. First organization in organizations array
 *
 * Write behavior:
 * - Updates _currentOrganizationIdAtom
 * - Side effects: updates sessionStorage, localStorage, axios headers
 *
 * Why not atomWithStorage?
 * - We write to BOTH sessionStorage AND localStorage (atomWithStorage supports only one)
 * - Additional side effects: axios headers (beyond storage)
 * - Complex initialization logic with priority chain and validation
 * - Storage reading happens inside the async atom
 * - No requirement for cross-tab sync (can add manually if needed later)
 *
 * Note: URL sync, cache clearing, and navigation are handled
 * separately in OrganizationsProvider to preserve complex logic.
 *
 * @throws Error if organizations array is empty
 */
const currentOrganizationIdAtom = atom(
  async (get) => {
    // Check if we have an explicit override value
    const currentOrganizationId = get(_currentOrganizationIdAtom);
    const organizations = await get(organizationsAtom);

    if (!organizations?.length) {
      throw new Error('Organizations array is empty');
    }

    // Helper to check if org exists in organizations array
    const findOrgById = (
      orgId: string | null | undefined
    ): Organization | undefined => {
      if (!orgId) return undefined;
      return organizations.find((org) => org.id === orgId);
    };
    const currentOrganization = findOrgById(currentOrganizationId);

    if (currentOrganizationId && currentOrganization)
      return currentOrganizationId;

    // TODO(UI-5822): Replace with determineCurrentOrganization after decoupling enterprise logic from organization.state.ts
    let resolvedOrgId: Organization['id'];

    // 1. Check search params
    const orgIdFromSearch = get(organizationSearchParamAtom);
    const orgFromSearch = findOrgById(orgIdFromSearch);

    if (orgFromSearch) {
      resolvedOrgId = orgFromSearch.id;
    }
    // 2. Check localStorage
    else {
      const orgIdFromLocalStorage = localStorage.getItem(organizationQueryKey);
      const orgFromLocalStorage = findOrgById(orgIdFromLocalStorage);
      if (orgFromLocalStorage) {
        resolvedOrgId = orgFromLocalStorage.id;
      }
      // 3. Check sessionStorage
      else {
        const orgIdFromSessionStorage =
          sessionStorage.getItem(organizationQueryKey);
        const orgFromSessionStorage = findOrgById(orgIdFromSessionStorage);
        if (orgFromSessionStorage) {
          resolvedOrgId = orgFromSessionStorage.id;
        }
        // 4. Fallback to first organization
        else {
          resolvedOrgId = organizations[0].id;
        }
      }
    }

    updateStorageAndHeaders(resolvedOrgId);

    return resolvedOrgId;
  },
  (get, set, newValue: Organization['id'] | undefined) => {
    const prevValue = get(_currentOrganizationIdAtom);

    // Update the atom value
    set(_currentOrganizationIdAtom, newValue);
    set(_viewedOrganizationIdAtom, newValue);

    // Side effects only if value actually changed
    if (newValue && newValue !== prevValue) {
      // Update storage and headers
      updateStorageAndHeaders(newValue);
      // Update search params
      set(organizationSearchParamAtom, newValue);
    }
  }
);

export const useCurrentOrganizationId = () =>
  useAtomValue(currentOrganizationIdAtom);

export const useSetCurrentOrganizationId = (): ((
  organizationId: Organization['id']
) => void) => {
  const set = useSetAtom(currentOrganizationIdAtom);
  return (organizationId: Organization['id']) => set(organizationId);
};
/**
 * Async atom that returns the current organization object.
 * Derives from currentOrganizationIdAtom and organizationKeyByIdAtom.
 */
export const currentOrganizationAtom = atom(async (get) => {
  const currentOrgId = await get(currentOrganizationIdAtom);
  if (!currentOrgId) return undefined;
  const organizationKeyById = get(organizationKeyByIdAtom);
  return organizationKeyById[currentOrgId];
});

export const currentOrganizationSyncAtom = unwrap(currentOrganizationAtom);

export const useCurrentOrganization = () =>
  useAtomValue(currentOrganizationAtom)!;

export const useOrganization = (
  id: Organization['id'] | null | undefined
): Organization | undefined => {
  const organizationKeyById = useOrganizationKeyById();

  return id && id in organizationKeyById ? organizationKeyById[id] : undefined;
};

/**
 * Main async atom for viewed organization ID with defaulting logic.
 *
 * Read behavior (async):
 * - If _viewedOrganizationIdAtom has a value, returns it immediately
 * - Otherwise, defaults to:
 *   1. currentOrganization.parentId (if it's a child organization)
 *   2. currentOrganization.id (if it's an enterprise or standalone org)
 *
 * This ensures that:
 * - Child organizations default to viewing their parent enterprise context
 * - Enterprise/standalone orgs default to viewing themselves
 * - Manual selection via ViewByOrganizationSelector overrides the default
 *
 * Write behavior:
 * - Updates _viewedOrganizationIdAtom with the new value
 *
 * @returns Organization ID to view, or undefined if currentOrganization is not loaded
 */
const viewedOrganizationIdAtom = atom(
  async (get) => {
    // Check if we have an explicit override value
    const viewedOrgId = get(_viewedOrganizationIdAtom);
    if (viewedOrgId) return viewedOrgId;

    // Otherwise, compute default from current organization
    const currentOrg = await get(currentOrganizationAtom);
    if (!currentOrg) return undefined;

    // Default to current organization id
    return currentOrg.id;
  },
  (get, set, newValue: Organization['id'] | undefined) => {
    set(_viewedOrganizationIdAtom, newValue);
  }
);

export const useSetViewedOrganizationId = (): ((
  organizationId: Organization['id']
) => void) => {
  const set = useSetAtom(viewedOrganizationIdAtom);
  return (organizationId: Organization['id']) => set(organizationId);
};

export const useViewedOrganization = () => {
  const viewedOrganizationId = useAtomValue(viewedOrganizationIdAtom);

  return useOrganization(viewedOrganizationId);
};

export const InitOrganization = () => {
  const organizations = useAtomValue(organizationsAtomUnwrapped);
  const currentOrganization = useCurrentOrganization();

  // Check if user has access to the organization or navigate to forbidden page
  useOrganizationAccessCheck({ organizations, currentOrganization });

  // Organization route validation - ensures users are on correct routes based on org type
  useOrganizationRouteValidation({
    currentOrganization,
  });

  return null;
};
